import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import EventService from '../../../services/EventService';
import { OfflineCache } from '../../services/OfflineCache';
import QrScanner from 'react-qr-scanner';

function EventCheckinAndroid() {
    const { id: eventId } = useParams();
    const [loading, setLoading] = useState(true);
    const [event, setEvent] = useState(null);
    const [scannerEnabled, setScannerEnabled] = useState(false);
    const [changedTickets, setChangedTickets] = useState([]);
    const [submitting, setSubmitting] = useState(false);
    const [processingScan, setProcessingScan] = useState(false);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');
    const [scannedAt, setScannedAt] = useState(null);

    useEffect(() => {
        const loadEventData = async () => {
            try {
                const response = await EventService.getEventCheckIn(eventId);
                const data = response.data;
                // Merge pending scans over server data to reflect local changes
                const pending = OfflineCache.getPendingScansForEvent(eventId) || [];
                const mergedTickets = OfflineCache.applyPendingScansToTickets(data.tickets || [], pending);
                setEvent({ ...data, tickets: mergedTickets });
                OfflineCache.saveEventTickets(eventId, mergedTickets);
            } catch (error) {
                // offline fallback
                const cachedTickets = OfflineCache.getEventTickets(eventId);
                if (cachedTickets && cachedTickets.length) {
                    const pending = OfflineCache.getPendingScansForEvent(eventId) || [];
                    const mergedTickets = OfflineCache.applyPendingScansToTickets(cachedTickets, pending);
                    setEvent({ eventName: '', ticketsSold: mergedTickets.length, tickets: mergedTickets });
                } else {
                    setError('Failed to load tickets');
                }
            } finally {
                setLoading(false);
            }
        };
        loadEventData();
    }, [eventId]);

    const submitTickets = async () => {
        setSubmitting(true);
        try {
            // Merge local changes with pending offline scans
            const pending = OfflineCache.getPendingScansForEvent(eventId) || [];
            const all = [...changedTickets, ...pending.map(p => ({ ticketId: p.ticketId, scanned: !!p.scanned }))];
            // Deduplicate by ticketId (prefer scanned=true if any)
            const map = new Map();
            for (const t of all) {
                const prev = map.get(t.ticketId);
                map.set(t.ticketId, prev ? { ticketId: t.ticketId, scanned: prev.scanned || t.scanned } : t);
            }
            const payload = Array.from(map.values());

            const isOnline = navigator.onLine;
            if (isOnline && payload.length > 0) {
                await EventService.submitChangedTickets(payload);
                // Clear local buffers on success
                setChangedTickets([]);
                OfflineCache.clearPendingScansForEvent(eventId);
                try {
                    const response = await EventService.getEventCheckIn(eventId);
                    setEvent(response.data);
                    OfflineCache.saveEventTickets(eventId, response.data.tickets || []);
                } catch (_) {}
                setError('');
                setSuccess('Changes synced');
            } else {
                // Offline: queue and mark success locally
                if (payload.length > 0) {
                    for (const p of payload) {
                        OfflineCache.addPendingScan({ eventId, ticketId: p.ticketId, scanned: p.scanned, scannedAt: new Date().toISOString() });
                    }
                    setChangedTickets([]);
                }
                setError('');
                setSuccess('Changes saved locally (offline)');
            }
        } catch (error) {
            const isNetworkError = !error?.response;
            if (isNetworkError) {
                // Treat as offline: queue all and report success locally
                const pending = OfflineCache.getPendingScansForEvent(eventId) || [];
                const all = [...changedTickets, ...pending.map(p => ({ ticketId: p.ticketId, scanned: !!p.scanned }))];
                const map = new Map();
                for (const t of all) {
                    const prev = map.get(t.ticketId);
                    map.set(t.ticketId, prev ? { ticketId: t.ticketId, scanned: prev.scanned || t.scanned } : t);
                }
                for (const p of Array.from(map.values())) {
                    OfflineCache.addPendingScan({ eventId, ticketId: p.ticketId, scanned: p.scanned, scannedAt: new Date().toISOString() });
                }
                setChangedTickets([]);
                setError('');
                setSuccess('Changes saved locally (offline)');
            } else {
                setError('Failed to submit tickets');
                setSuccess('');
            }
        } finally {
            setSubmitting(false);
        }
    };

    const submitScannedTicket = async (ticketUrl) => {
        const ticketId = (ticketUrl || '').split('/').pop();
        if (!ticketId) return;
        setSubmitting(true);
        setProcessingScan(true);
        // Close camera immediately after we captured a QR to avoid overlap and duplicate scans
        setScannerEnabled(false);
        try {
            const isOnline = navigator.onLine;
            if (!isOnline) {
                const cached = OfflineCache.getEventTickets(eventId) || [];
                const found = cached.find(t => t.ticketId === ticketId);
                if (!found) {
                    setError('Ticket not found locally');
                    setSuccess('');
                } else if (found.scanned) {
                    setError('Ticket has already been scanned');
                    setSuccess('');
                    setScannedAt(found.scannedAt || null);
                } else {
                    const ts = new Date().toISOString();
                    setError('');
                    setSuccess('Scan successful (offline)');
                    setScannedAt(ts);
                    setEvent(prev => {
                        if (!prev) return prev;
                        const updated = { ...prev, tickets: (prev.tickets || []).map(t => t.ticketId === ticketId ? { ...t, scanned: true, scannedAt: ts } : t) };
                        OfflineCache.saveEventTickets(eventId, updated.tickets);
                        return updated;
                    });
                    OfflineCache.addPendingScan({ eventId, ticketId, scanned: true, scannedAt: ts });
                }
                return;
            }
            const res = await EventService.scanTicket(ticketId, eventId);
            const body = res?.data || {};
            const msg = body.message || body.status || body.result || (typeof body === 'string' ? body : '') || 'Scan successful';
            setScannedAt(body?.scannedAt || null);
            if (body?.success === true) {
                setError('');
                setSuccess(msg);
                // apply locally and cache
                const ts = body?.scannedAt || new Date().toISOString();
                setEvent(prev => {
                    if (!prev) return prev;
                    const updated = { ...prev, tickets: (prev.tickets || []).map(t => t.ticketId === ticketId ? { ...t, scanned: true, scannedAt: ts } : t) };
                    OfflineCache.saveEventTickets(eventId, updated.tickets);
                    return updated;
                });
                OfflineCache.addPendingScan({ eventId, ticketId, scanned: true, scannedAt: body?.scannedAt || new Date().toISOString() });
            } else {
                setError(msg);
                setSuccess('');
                // keep pending scan too to try later if offline or error
                OfflineCache.addPendingScan({ eventId, ticketId, scanned: true, scannedAt: body?.scannedAt || new Date().toISOString() });
            }
        } catch (error) {
            const isNetworkError = !error?.response;
            if (isNetworkError) {
                const cached = OfflineCache.getEventTickets(eventId) || [];
                const found = cached.find(t => t.ticketId === ticketId);
                if (!found) {
                    setError('Ticket not found locally');
                    setSuccess('');
                } else if (found.scanned) {
                    setError('Ticket has already been scanned');
                    setSuccess('');
                    setScannedAt(found.scannedAt || null);
                } else {
                    const ts = new Date().toISOString();
                    setError('');
                    setSuccess('Scan successful (offline)');
                    setScannedAt(ts);
                    setEvent(prev => {
                        if (!prev) return prev;
                        const updated = { ...prev, tickets: (prev.tickets || []).map(t => t.ticketId === ticketId ? { ...t, scanned: true, scannedAt: ts } : t) };
                        OfflineCache.saveEventTickets(eventId, updated.tickets);
                        return updated;
                    });
                    OfflineCache.addPendingScan({ eventId, ticketId, scanned: true, scannedAt: ts });
                }
            } else {
                const serverErr = error?.response?.data?.message || error?.response?.data || error?.message || 'Failed to scan ticket';
                setError(serverErr);
                setSuccess('');
                setScannedAt(error?.response?.data?.scannedAt || null);
            }
        } finally {
            setSubmitting(false);
            setProcessingScan(false);
        }
    };

    const handleScan = (data) => {
        if (!data || processingScan) return;
        const text = typeof data === 'string' ? data : (data.text || data.data || '');
        if (!text) return;
        submitScannedTicket(text);
    };

    const handleError = () => setError('Scanner error');

    return (
        <div className="px-4 py-4">
            {loading ? (
                <div className="text-center py-10">Loading...</div>
            ) : !event ? (
                <div className="text-center py-10">Not found</div>
            ) : (
                <div>
                    {error ? (
                        <div className="mb-4 rounded-lg border border-red-300 bg-red-50 text-red-800 p-4">
                            <p className="font-semibold text-base">{error}</p>
                            {scannedAt ? (
                                <p className="text-sm mt-1">Scanned at: {new Date(scannedAt).toLocaleString('de-DE')}</p>
                            ) : null}
                        </div>
                    ) : null}
                    {success ? (
                        <div className="mb-4 rounded-lg border border-green-300 bg-green-50 text-green-800 p-4">
                            <p className="font-semibold text-base">{success}</p>
                            {scannedAt ? (
                                <p className="text-sm mt-1">Scanned at: {new Date(scannedAt).toLocaleString('de-DE')}</p>
                            ) : null}
                        </div>
                    ) : null}

                    {scannerEnabled && (
                        <div className="rounded overflow-hidden border mb-4">
                            <QrScanner delay={250} onError={handleError} onScan={handleScan} style={{ width: '100%' }} constraints={{ video: { facingMode: 'environment' } }} />
                        </div>
                    )}

                    <div className="flex gap-2 mb-3">
                        <button className="flex-1 bg-blue-600 text-white rounded-md px-4 py-3 text-base" onClick={() => setScannerEnabled(v => !v)} disabled={processingScan}>
                            {scannerEnabled ? 'Close Scanner' : 'Open Scanner'}
                        </button>
                        <button className="flex-1 bg-green-600 text-white rounded-md px-4 py-3 text-base" onClick={submitTickets} disabled={submitting}>
                            {submitting ? 'Submitting...' : 'Save Changes'}
                        </button>
                    </div>

                    <div className="rounded-md border divide-y">
                        {Array.isArray(event.tickets) && event.tickets.map((t) => (
                            <label key={t.ticketId} className="flex items-center justify-between px-4 py-3">
                                <div>
                                    <p className="font-medium">{t.ticketName}</p>
                                    <p className="text-sm text-gray-500">{t.email || t.mail || t.customerMail || ''}</p>
                                    {t.scannedAt ? (
                                        <p className="text-xs text-gray-400">Scanned at: {new Date(t.scannedAt).toLocaleString('de-DE')}</p>
                                    ) : null}
                                </div>
                                <input
                                    type="checkbox"
                                    checked={!!t.scanned}
                                    onChange={() => {
                                        const nowChecked = !t.scanned;
                                        const ts = nowChecked ? new Date().toISOString() : null;
                                        const updatedTickets = event.tickets.map(x => x.ticketId === t.ticketId ? { ...x, scanned: nowChecked, scannedAt: ts } : x);
                                        setEvent({ ...event, tickets: updatedTickets });
                                        OfflineCache.saveEventTickets(eventId, updatedTickets);
                                        setChangedTickets((prev) => {
                                            const idx = prev.findIndex(y => y.ticketId === t.ticketId);
                                            if (idx > -1) {
                                                const clone = [...prev];
                                                clone[idx] = { ...clone[idx], scanned: nowChecked };
                                                return clone;
                                            }
                                            return [...prev, { ticketId: t.ticketId, scanned: nowChecked }];
                                        });
                                        OfflineCache.addPendingScan({ eventId, ticketId: t.ticketId, scanned: nowChecked, scannedAt: ts });
                                    }}
                                />
                            </label>
                        ))}
                    </div>
                </div>
            )}
        </div>
    );
}

export default EventCheckinAndroid;


